'use client';
import * as React from 'react';
import { useRouter } from 'next/router';
import { useTranslation, Trans } from 'next-i18next';
import { useSessionUser } from '@/hooks/useSessionUser';
import Loading from '@/components/Loading/Loading';
import Link from 'next/link';
import { useAudio } from '@/context/AudioContext';
import SettingModal from '@/components/_modals/SettingModal';
import { Tooltip } from 'react-tooltip';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import InitAppModal from '@/components/_modals/InitAppModal';
import EdgeButton from '@/components/_buttons/EdgeButton';
import Image from 'next/image';
import styled from 'styled-components';
import useAskCount from '@/hooks/stores/useAskCountStore';
import PremiumModal from '@/components/_modals/PremiumModal';
import ChargeModal from '@/components/_modals/ChargeModal';

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
`;

const imageClassName = {
  jp: 'w-[300px] h-[150px]',
  us: 'w-[300px] h-[150px]',
};

export default function Page() {
  const { user } = useSessionUser();
  const router = useRouter();
  const { t, i18n } = useTranslation();
  const { isPremium, steamUser, setPremium } = useAskCount();
  const [isFirstLaunch, setIsFirstLaunch] = React.useState(true);
  const { playSoundEffect, playBgm } = useAudio();
  const [isDisplaySettingModal, setDisplaySettingModal] = React.useState(false);
  const [isDisplayPremiumModal, setDisplayPremiumModal] = React.useState(false);
  const [displayRoomMenu, setDisplayRoomMenu] = React.useState(false);
  const [hasInteracted, setHasInteracted] = React.useState(false);
  const [isDisplayChargeModal, setDisplayChargeModal] = React.useState(false);

  const handleCloseLaunchModal = () => {
    setIsFirstLaunch(false);
    localStorage.setItem('launched', 'true');
  };

  const handleCloseRoomMenu = () => {
    if (!displayRoomMenu) return;
    setDisplayRoomMenu(false);
  };

  const handleShowSettingModal = React.useCallback(() => {
    setDisplaySettingModal(true);
  }, []);

  const handleCloseSettingModal = React.useCallback(() => {
    setDisplaySettingModal(false);
  }, []);

  const handleShowPremiumModal = React.useCallback(() => {
    setDisplayPremiumModal(true);
  }, []);

  const handleClosePremiumModal = React.useCallback(() => {
    setDisplayPremiumModal(false);
  }, []);

  const handleShowChargeModal = () => {
    setDisplayChargeModal(true);
  };

  const handleCloseChargeModal = () => {
    setDisplayChargeModal(false);
  };

  React.useEffect(() => {
    setIsFirstLaunch(!localStorage.getItem('launched'));
    window.onPremiumStatusChanged = (isPremium: boolean) => {
      // 課金成功時の処理
      if (isPremium) {
        setPremium(true);
      } else {
        setPremium(false);
      }
    };
  }, [i18n.language]);

  React.useEffect(() => {
    if (router.query.name) {
      localStorage.setItem('userName', String(router.query.name));
      localStorage.setItem('launched', 'true');
      setIsFirstLaunch(false);
    }
  }, [router.query.name]);

  return (
    <>
      {user ? (
        <>
          {isFirstLaunch ? (
            <InitAppModal onClose={handleCloseLaunchModal} />
          ) : (
            <div className="relative h-full flex flex-col justify-start items-center galaxy-h:gap-4">
              <div className="h-full flex flex-col justify-center items-center gap-8 text-gold-300 mb-10">
                <div className="relative flex justify-center pb-4">
                  <img
                    className={`${
                      imageClassName[i18n.language as 'jp' | 'us']
                    }`}
                    src={`/images/title_logo_${i18n.language}1.png`}
                    alt="logo"
                  />
                </div>

                <div className="flex flex-col items-center gap-4">
                  <Link href={'/lobbies'}>
                    <div className="relative flex flex-col items-center">
                      <div className="text-xl">{t('menu.playGame')}</div>
                      <div
                        className='select-none bg-[url("/images/menu_line.png")] w-[220px] h-4 bg-no-repeat  absolute -bottom-4'
                        style={{ backgroundSize: '100% 100%' }}
                      />
                    </div>
                  </Link>

                  <Link href={'/original-questions'}>
                    <div className="relative flex flex-col items-center">
                      <div className="text-xl">
                        {t('menu.originalQuestions')}
                      </div>
                      <div
                        className='select-none bg-[url("/images/menu_line.png")] w-[220px] h-4 bg-no-repeat  absolute -bottom-4'
                        style={{ backgroundSize: '100% 100%' }}
                      />
                    </div>
                  </Link>
                  <div
                    className="relative flex flex-col items-center"
                    onClick={handleShowSettingModal}
                  >
                    <div className="text-xl">{t('menu.options')}</div>
                    <div
                      className='select-none bg-[url("/images/menu_line.png")] w-[220px] h-4 bg-no-repeat  absolute -bottom-4'
                      style={{ backgroundSize: '100% 100%' }}
                    />
                  </div>
                  {!steamUser && !isPremium && (
                    <div
                      className="relative flex flex-col items-center"
                      onClick={handleShowPremiumModal}
                    >
                      <div className="text-xl">{t('menu.premium')}</div>
                      <div
                        className='select-none bg-[url("/images/menu_line.png")] w-[220px] h-4 bg-no-repeat  absolute -bottom-4'
                        style={{ backgroundSize: '100% 100%' }}
                      />
                    </div>
                  )}
                  {!steamUser && !isPremium && (
                    <div
                      className="relative flex flex-col items-center"
                      onClick={handleShowChargeModal}
                    >
                      <div className="text-xl">{t('menu.charge')}</div>
                      <div
                        className='select-none bg-[url("/images/menu_line.png")] w-[220px] h-4 bg-no-repeat  absolute -bottom-4'
                        style={{ backgroundSize: '100% 100%' }}
                      />
                    </div>
                  )}
                  <div className="flex flex-col items-start gap-4 fixed bottom-6">
                    <a
                      href={t('discordLink')}
                      className="room-icon flex hover-scale-1_3 items-center gap-2 justify-end text-sm  text-brown-secondary font-bold cursor-pointer active-scale-90"
                      style={{ letterSpacing: '2px' }}
                    >
                      <Image
                        src="/images/icon_discord.svg"
                        width={32}
                        height={32}
                        alt="to home"
                      />
                      {t('discord')}{' '}
                    </a>
                  </div>
                </div>
              </div>

              {isDisplaySettingModal && (
                <SettingModal onClose={handleCloseSettingModal} />
              )}
              {isDisplayPremiumModal && (
                <PremiumModal onClose={handleClosePremiumModal} />
              )}
              {isDisplayChargeModal && (
                <ChargeModal onClose={handleCloseChargeModal} />
              )}
              <Tooltip id="my-tooltip" className="z-10" />
            </div>
          )}
        </>
      ) : (
        <>
          <Loading title="Loading" size={8} />
        </>
      )}
    </>
  );
}

export async function getStaticProps({ locale }: { locale: string }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'], null, [
        'us',
        'jp',
        'kr',
      ])),
    },
  };
}
